/**
 * ProposalPoCoverage — multiple client POs against one proposal.
 *
 * Shows every proposal charge (Setup / Annual AMC / Annual Cost per item),
 * which linked PO covers it and for which period, plus the linked POs with
 * their own client PO number, date, terms and attachment. Operators can
 * raise a new PO from selected charges or link an existing PO; charges are
 * never matched by amount — the operator picks them explicitly.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowsLeftRight, LinkSimple, CheckCircle, WarningCircle, Paperclip } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { formatINR, formatDate } from "../../lib/format";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import FormDialog, { FormDialogCancel } from "../FormDialog";
import { DialogFooter } from "../ui/dialog";

const BUCKET_TONE = { setup: "bg-slate-100 text-slate-700", amc: "bg-emerald-50 text-emerald-800", annual_cost: "bg-sky-50 text-sky-800" };
const BUCKET_LABEL = { setup: "Software & Set-up", amc: "Annual AMC", annual_cost: "Annual Cost" };

const chargeAmount = (c, cur) => (c.complimentary ? "Complimentary" : formatINR(c.amount_excl_gst, cur));

function useCoverage(proposalId, deps = []) {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const reload = async () => {
        try {
            const r = await api.get(`/billing/proposal/${proposalId}/po-coverage`);
            setData(r.data);
            setErr(null);
        } catch (e) {
            setErr(formatApiError(e));
        }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { if (proposalId) reload(); }, [proposalId, ...deps]);
    return { data, err, reload };
}

function BucketChip({ c }) {
    return (
        <span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${BUCKET_TONE[c.bucket]}`}>
            {c.bucket_label || BUCKET_LABEL[c.bucket]}
        </span>
    );
}

function ChargePicker({ charges, selected, onToggle, currency, extra }) {
    const groups = useMemo(() => {
        const m = new Map();
        for (const c of charges) {
            const k = c.option_label || "Items";
            if (!m.has(k)) m.set(k, []);
            m.get(k).push(c);
        }
        return [...m.entries()];
    }, [charges]);
    return (
        <div className="border border-slate-200 rounded-md max-h-72 overflow-y-auto divide-y divide-slate-100" data-testid="charge-picker">
            {groups.map(([band, rows]) => (
                <div key={band}>
                    <div className="px-3 py-1.5 bg-slate-50 reedos-eyebrow text-[9px]">{band}</div>
                    {rows.map((c) => {
                        const busy = c.status === "allocated";
                        return (
                            <div key={c.key} className="px-3 py-2 flex items-center gap-3 text-sm" data-testid={`charge-row-${c.key}`}>
                                <input
                                    type="checkbox" className="h-4 w-4 accent-slate-800"
                                    checked={!!selected[c.key]} disabled={busy}
                                    onChange={(e) => onToggle(c, e.target.checked)}
                                    data-testid={`charge-select-${c.key}`}
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="truncate text-slate-800">{c.label}</div>
                                    <div className="flex items-center gap-1.5 mt-0.5">
                                        <BucketChip c={c} />
                                        {c.allocations.map((a) => (
                                            <span key={a.id} className="text-[10px] text-amber-700">
                                                PO {a.client_po_number || a.po_number}{a.coverage_start ? ` · ${formatDate(a.coverage_start)} → ${formatDate(a.coverage_end)}` : ""}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                                <div className="text-right font-mono text-xs text-slate-700 w-28">{chargeAmount(c, currency)}</div>
                                {extra && selected[c.key] && extra(c)}
                            </div>
                        );
                    })}
                </div>
            ))}
        </div>
    );
}

function CoverageDates({ value, onChange, prefix }) {
    return (
        <div className="grid grid-cols-2 gap-3">
            <div>
                <Label className="text-xs font-semibold">Coverage start (recurring charges)</Label>
                <Input type="date" value={value.start} onChange={(e) => onChange({ ...value, start: e.target.value })} className="mt-1" data-testid={`${prefix}-coverage-start`} />
            </div>
            <div>
                <Label className="text-xs font-semibold">Coverage end</Label>
                <Input type="date" value={value.end} onChange={(e) => onChange({ ...value, end: e.target.value })} className="mt-1" data-testid={`${prefix}-coverage-end`} />
            </div>
            <p className="col-span-2 text-[11px] text-slate-500">
                The service period agreed for these charges — it is never taken from the PO date. Leave blank only if not yet agreed (blocks any other PO for the same charge).
            </p>
        </div>
    );
}

function selectedTotal(charges, selected) {
    return charges.filter((c) => selected[c.key]).reduce((s, c) => s + (Number(c.amount_excl_gst) || 0), 0);
}

const BUCKETS = ["setup", "amc", "annual_cost"];
const DEFAULT_LINE_DESC = { setup: "Software & Set-up Fee", amc: "AMC Fee", annual_cost: "Annual Cost" };
const OWN = "own";

/* Suggested PO lines: two or more priced charges of one type → one line;
   single, Complimentary and package charges stay as their own line. */
function suggestLayout(sel) {
    const groups = [];
    const assign = {};
    for (const b of BUCKETS) {
        const rows = sel.filter((c) => c.bucket === b && !c.complimentary && !c.package);
        if (rows.length >= 2) {
            const id = `g-${b}-1`;
            groups.push({ id, bucket: b, description: DEFAULT_LINE_DESC[b] });
            rows.forEach((c) => { assign[c.key] = id; });
        }
    }
    sel.forEach((c) => { if (!assign[c.key]) assign[c.key] = OWN; });
    return { groups, assign };
}

export function layoutToPoLines(layout) {
    return layout.groups
        .map((g) => ({
            description: (g.description || "").trim() || DEFAULT_LINE_DESC[g.bucket],
            charge_keys: Object.keys(layout.assign).filter((k) => layout.assign[k] === g.id),
        }))
        .filter((g) => g.charge_keys.length > 0);
}

function PoLineLayout({ charges, layout, setLayout, currency }) {
    const { groups, assign } = layout;
    const groupTotal = (g) => charges.filter((c) => assign[c.key] === g.id).reduce((s, c) => s + (Number(c.amount_excl_gst) || 0), 0);
    const live = groups.filter((g) => charges.some((c) => assign[c.key] === g.id));
    const ownCount = charges.filter((c) => assign[c.key] === OWN).length;
    const move = (c, target) => {
        if (target !== "new") { setLayout({ groups, assign: { ...assign, [c.key]: target } }); return; }
        const id = `g-${c.bucket}-${groups.filter((g) => g.bucket === c.bucket).length + 1}-${Date.now() % 10000}`;
        setLayout({ groups: [...groups, { id, bucket: c.bucket, description: DEFAULT_LINE_DESC[c.bucket] }], assign: { ...assign, [c.key]: id } });
    };
    const rename = (g, description) => setLayout({ assign, groups: groups.map((x) => (x.id === g.id ? { ...x, description } : x)) });
    return (
        <div className="rounded-md border border-slate-200 p-3 space-y-3" data-testid="po-line-layout">
            <div className="flex items-center justify-between gap-2">
                <div>
                    <Label className="text-xs font-semibold">PO lines</Label>
                    <p className="text-[11px] text-slate-500">Grouped lines show one combined amount; each underlying proposal charge stays linked and is counted once.</p>
                </div>
                <div className="flex gap-1.5 shrink-0">
                    <Button type="button" size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setLayout(suggestLayout(charges))} data-testid="po-layout-suggest">Suggested grouping</Button>
                    <Button type="button" size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setLayout({ groups: [], assign: Object.fromEntries(charges.map((c) => [c.key, OWN])) })} data-testid="po-layout-itemise">Keep itemised</Button>
                </div>
            </div>
            {live.map((g) => (
                <div key={g.id} className="flex items-center gap-2" data-testid={`po-group-${g.id}`}>
                    <BucketChip c={g} />
                    <Input value={g.description} onChange={(e) => rename(g, e.target.value)} maxLength={200} className="h-8 text-sm flex-1" data-testid={`po-group-desc-${g.id}`} />
                    <span className="text-[11px] text-slate-500 whitespace-nowrap">{charges.filter((c) => assign[c.key] === g.id).length} charges</span>
                    <span className="font-mono text-xs font-semibold w-28 text-right" data-testid={`po-group-amount-${g.id}`}>{formatINR(groupTotal(g), currency)}</span>
                </div>
            ))}
            {ownCount > 0 && <div className="text-[11px] text-slate-500" data-testid="po-layout-own-count">{ownCount} charge{ownCount === 1 ? "" : "s"} kept as their own line, as in the proposal.</div>}
            <div className="max-h-48 overflow-y-auto divide-y divide-slate-100 border-t border-slate-100">
                {charges.map((c) => (
                    <div key={c.key} className="py-1.5 flex items-center gap-2 text-xs" data-testid={`po-line-assign-row-${c.key}`}>
                        <span className="flex-1 truncate text-slate-700">{c.label}</span>
                        <BucketChip c={c} />
                        <span className="font-mono w-24 text-right">{chargeAmount(c, currency)}</span>
                        <select
                            value={assign[c.key] || OWN} onChange={(e) => move(c, e.target.value)} disabled={c.package}
                            className="h-7 max-w-[190px] rounded-md border border-slate-200 bg-white px-1.5 text-[11px]" data-testid={`po-line-assign-${c.key}`}
                        >
                            <option value={OWN}>Own line (as in proposal)</option>
                            {groups.filter((g) => g.bucket === c.bucket).map((g) => <option key={g.id} value={g.id}>{g.description || DEFAULT_LINE_DESC[g.bucket]}</option>)}
                            <option value="new">+ New combined line</option>
                        </select>
                    </div>
                ))}
            </div>
        </div>
    );
}

export function ConvertChargesDialog({ proposal, onDone, label = "Convert to PO", testid = "convert-btn" }) {
    const [open, setOpen] = useState(false);
    const { data, reload } = useCoverage(open ? proposal.id : null);
    const [selected, setSelected] = useState({});
    const [form, setForm] = useState({ po_number: "", client_po_number: "", client_po_date: "", client_po_terms: "", remarks: "" });
    const [dates, setDates] = useState({ start: "", end: "" });
    const [busy, setBusy] = useState(false);
    const [layout, setLayout] = useState({ groups: [], assign: {} });
    const charges = data?.charges || [];
    const selectedCharges = charges.filter((c) => selected[c.key]);
    const selKey = selectedCharges.map((c) => c.key).join("|");
    useEffect(() => {
        if (!open || !data) return;
        const init = {};
        for (const c of data.charges) if (c.status === "available") init[c.key] = true;
        setSelected(init);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, data?.version_id]);
    // Re-suggest the PO line grouping whenever the selected charges change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { setLayout(suggestLayout(selectedCharges)); }, [selKey]);
    const anyRecurring = charges.some((c) => selected[c.key] && c.recurring);
    const submit = async () => {
        const keys = Object.keys(selected).filter((k) => selected[k]);
        if (!keys.length) { toast.error("Select at least one charge"); return; }
        setBusy(true);
        try {
            const { data: res } = await api.post(`/billing/proposal/${proposal.id}/convert-to-po`, {
                charge_keys: keys,
                po_lines: layoutToPoLines(layout),
                po_number: form.po_number || null,
                remarks: form.remarks,
                client_po_number: form.client_po_number || null,
                client_po_date: form.client_po_date || null,
                client_po_received: !!form.client_po_number,
                po_source_type: form.client_po_number ? "client_provided" : "system_generated",
                client_po_terms: form.client_po_terms || null,
                coverage_start: anyRecurring && dates.start ? dates.start : null,
                coverage_end: anyRecurring && dates.end ? dates.end : null,
            });
            toast.success(`Created ${res.po.po_number}`);
            setOpen(false);
            onDone?.(res.po);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to create PO"));
            reload();
        } finally {
            setBusy(false);
        }
    };
    return (
        <>
            <Button size="sm" onClick={() => setOpen(true)} className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5" data-testid={testid}>
                <ArrowsLeftRight size={14} /> {label}
            </Button>
            <FormDialog
                open={open} onOpenChange={setOpen} isDirty={false} contentClassName="max-w-3xl"
                testId="proposal-convert-dialog" title="Create PO from proposal charges"
                description="Pick the charges this client PO covers. Unselected charges stay available for further POs (e.g. a separate AMC or hosting PO)."
            >
                {!data ? <div className="text-sm text-slate-500 py-6">Loading charges…</div> : (
                    <div className="space-y-4">
                        {data.legacy_unmapped_po_id && <LegacyPoNotice />}
                        <ChargePicker charges={charges} selected={selected} currency={proposal.currency}
                            onToggle={(c, v) => setSelected((p) => ({ ...p, [c.key]: v }))} />
                        <div className="text-xs text-slate-600" data-testid="convert-selected-total">
                            Selected: <span className="font-semibold">{formatINR(selectedTotal(charges, selected), proposal.currency)}</span> excl. GST
                        </div>
                        {selectedCharges.length > 0 && (
                            <PoLineLayout charges={selectedCharges} layout={layout} setLayout={setLayout} currency={proposal.currency} />
                        )}
                        <div className="grid grid-cols-3 gap-3">
                            <div>
                                <Label className="text-xs font-semibold">Client PO number</Label>
                                <Input value={form.client_po_number} onChange={(e) => setForm({ ...form, client_po_number: e.target.value })} className="mt-1 font-mono" data-testid="convert-client-po-number" />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold">Client PO date</Label>
                                <Input type="date" value={form.client_po_date} onChange={(e) => setForm({ ...form, client_po_date: e.target.value })} className="mt-1" data-testid="convert-client-po-date" />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold">Internal PO number (optional)</Label>
                                <Input value={form.po_number} onChange={(e) => setForm({ ...form, po_number: e.target.value })} placeholder="auto" className="mt-1 font-mono" data-testid="convert-po-number-input" />
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs font-semibold">Client PO terms</Label>
                            <Input value={form.client_po_terms} onChange={(e) => setForm({ ...form, client_po_terms: e.target.value })} placeholder="e.g. Payment processing within 15 days" className="mt-1" data-testid="convert-client-po-terms" />
                        </div>
                        {anyRecurring && <CoverageDates value={dates} onChange={setDates} prefix="convert" />}
                        <div>
                            <Label className="text-xs font-semibold">Remarks (V1 of new PO)</Label>
                            <Textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} rows={2} className="mt-1" />
                        </div>
                    </div>
                )}
                <DialogFooter>
                    <FormDialogCancel testId="convert-cancel-btn">Cancel</FormDialogCancel>
                    <Button onClick={submit} disabled={busy || !data || !!data?.legacy_unmapped_po_id} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="convert-confirm-btn">
                        {busy ? "Creating…" : "Create PO"}
                    </Button>
                </DialogFooter>
            </FormDialog>
        </>
    );
}

function LinkPoDialog({ proposal, coverage, onDone }) {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState([]);
    const [poId, setPoId] = useState("");
    const [po, setPo] = useState(null);
    const [selected, setSelected] = useState({});
    const [lineFor, setLineFor] = useState({});
    const [dates, setDates] = useState({ start: "", end: "" });
    const [variance, setVariance] = useState("");
    const [busy, setBusy] = useState(false);
    const linkedIds = new Set((coverage?.linked_pos || []).filter((x) => x.mapping_recorded).map((x) => x.po_id));
    useEffect(() => {
        if (!open) return;
        api.get(`/billing/po/list?client_id=${proposal.client_id}`).then((r) => setPos(r.data.items || [])).catch((e) => toast.error(formatApiError(e)));
    }, [open, proposal.client_id]);
    useEffect(() => {
        setPo(null); setLineFor({});
        if (!poId) return;
        api.get(`/billing/po/${poId}`).then((r) => setPo(r.data)).catch((e) => toast.error(formatApiError(e)));
    }, [poId]);
    const poLines = useMemo(() => {
        const vs = po?.versions || [];
        const v = vs.find((x) => x.is_final_version) || vs[vs.length - 1];
        return (v?.line_items || []).filter((li) => li.row_kind !== "product_header");
    }, [po]);
    const charges = coverage?.charges || [];
    const anyRecurring = charges.some((c) => selected[c.key] && c.recurring);
    const mapAll = (lineId) => setLineFor(Object.fromEntries(Object.keys(selected).filter((k) => selected[k]).map((k) => [k, lineId])));
    const submit = async () => {
        const keys = Object.keys(selected).filter((k) => selected[k]);
        if (!poId || !keys.length) { toast.error("Pick a PO and at least one charge"); return; }
        setBusy(true);
        try {
            await api.post(`/billing/proposal/${proposal.id}/po-links`, {
                po_id: poId,
                variance_note: variance.trim() || null,
                allocations: keys.map((k) => {
                    const c = charges.find((x) => x.key === k);
                    return {
                        charge_key: k,
                        po_line_id: lineFor[k] || null,
                        coverage_start: c?.recurring && dates.start ? dates.start : null,
                        coverage_end: c?.recurring && dates.end ? dates.end : null,
                    };
                }),
            });
            toast.success("PO linked to proposal charges");
            setOpen(false);
            onDone?.();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to link PO"));
        } finally {
            setBusy(false);
        }
    };
    const lineSelect = (c) => (
        <select
            value={lineFor[c.key] || ""} onChange={(e) => setLineFor((p) => ({ ...p, [c.key]: e.target.value }))}
            className="h-8 max-w-[180px] rounded-md border border-slate-200 bg-white px-2 text-xs" data-testid={`link-line-select-${c.key}`}
        >
            <option value="">Whole PO</option>
            {poLines.map((li) => <option key={li.id} value={li.id}>{li.product_name}</option>)}
        </select>
    );
    return (
        <>
            <Button size="sm" variant="outline" onClick={() => setOpen(true)} className="gap-1.5" data-testid="link-existing-po-btn">
                <LinkSimple size={14} /> Link existing PO
            </Button>
            <FormDialog
                open={open} onOpenChange={setOpen} isDirty={false} contentClassName="max-w-3xl"
                testId="link-po-dialog" title="Link an existing PO to proposal charges"
                description="The PO, its invoices and schedules stay exactly as they are — only the coverage of the selected charges is recorded. A consolidated PO line (e.g. one “AMC FEE” line) can cover several charges."
            >
                <div className="space-y-4">
                    <div>
                        <Label className="text-xs font-semibold">PO</Label>
                        <select value={poId} onChange={(e) => setPoId(e.target.value)} className="mt-1 flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="link-po-select">
                            <option value="">Select a PO of {proposal.client?.name || "this client"}…</option>
                            {pos.map((x) => (
                                <option key={x.id} value={x.id}>
                                    {x.client_po_number || x.po_number} · {formatINR(x.billing_status?.po_excl_gst, x.currency)} excl. GST{linkedIds.has(x.id) ? " · already mapped" : ""}
                                </option>
                            ))}
                        </select>
                    </div>
                    {po && (
                        <>
                            <div className="flex items-center gap-2 text-xs">
                                <span className="text-slate-600">Map all selected charges to:</span>
                                <select onChange={(e) => mapAll(e.target.value)} defaultValue="" className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs" data-testid="link-map-all-select">
                                    <option value="">Whole PO</option>
                                    {poLines.map((li) => <option key={li.id} value={li.id}>{li.product_name}</option>)}
                                </select>
                            </div>
                            <ChargePicker charges={charges} selected={selected} currency={proposal.currency} extra={lineSelect}
                                onToggle={(c, v) => setSelected((p) => ({ ...p, [c.key]: v }))} />
                            <div className="text-xs text-slate-600" data-testid="link-selected-total">
                                Selected charges: <span className="font-semibold">{formatINR(selectedTotal(charges, selected), proposal.currency)}</span>
                                {" "}· PO value: <span className="font-semibold">{formatINR(po.billing_status?.po_excl_gst, po.currency)}</span> excl. GST
                            </div>
                            {anyRecurring && <CoverageDates value={dates} onChange={setDates} prefix="link" />}
                            <div>
                                <Label className="text-xs font-semibold">Variance note (only if mapped charges legitimately differ from the PO value)</Label>
                                <Input value={variance} onChange={(e) => setVariance(e.target.value)} className="mt-1" data-testid="link-variance-note" />
                            </div>
                        </>
                    )}
                </div>
                <DialogFooter>
                    <FormDialogCancel testId="link-cancel-btn">Cancel</FormDialogCancel>
                    <Button onClick={submit} disabled={busy || !po} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="link-confirm-btn">
                        {busy ? "Linking…" : "Link PO"}
                    </Button>
                </DialogFooter>
            </FormDialog>
        </>
    );
}

function LinkedPoTable({ rows, currency }) {
    if (!rows.length) return <div className="text-xs text-slate-500 italic" data-testid="linked-pos-empty">No POs linked yet.</div>;
    return (
        <table className="w-full text-xs" data-testid="linked-pos-table">
            <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left">
                    <th className="px-3 py-2 reedos-eyebrow">Client PO</th>
                    <th className="px-3 py-2 reedos-eyebrow">Date</th>
                    <th className="px-3 py-2 reedos-eyebrow">Terms</th>
                    <th className="px-3 py-2 reedos-eyebrow text-right">PO value</th>
                    <th className="px-3 py-2 reedos-eyebrow text-right">Covers</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((x) => (
                    <tr key={x.po_id} className="border-t border-slate-100" data-testid={`linked-po-row-${x.po_id}`}>
                        <td className="px-3 py-2">
                            <Link to={`/purchase-orders/${x.po_id}`} className="font-mono font-semibold text-slate-900 underline-offset-2 hover:underline">
                                {x.client_po_number || x.po_number}
                            </Link>
                            <div className="text-[10px] text-slate-500 flex items-center gap-1">
                                {x.po_number}
                                {x.client_po_document && <span className="inline-flex items-center gap-0.5"><Paperclip size={10} />{x.client_po_document.filename || "PO copy"}</span>}
                            </div>
                        </td>
                        <td className="px-3 py-2">{x.client_po_date ? formatDate(x.client_po_date) : "—"}</td>
                        <td className="px-3 py-2 text-slate-600 max-w-[200px]">{x.client_po_terms || "—"}</td>
                        <td className="px-3 py-2 text-right font-mono">{formatINR(x.po_excl_gst, currency)}</td>
                        <td className="px-3 py-2 text-right" data-testid={`linked-po-mapped-${x.po_id}`}>
                            {x.mapping_recorded
                                ? <span className="font-mono font-semibold">{formatINR(x.mapped_excl_gst, currency)}</span>
                                : <span className="text-amber-700">Charges not mapped yet</span>}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function ChargeCoverageTable({ charges, currency, onRelease }) {
    return (
        <table className="w-full text-xs" data-testid="charge-coverage-table">
            <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left">
                    <th className="px-3 py-2 reedos-eyebrow">Charge</th>
                    <th className="px-3 py-2 reedos-eyebrow text-right">Amount</th>
                    <th className="px-3 py-2 reedos-eyebrow">Covered by</th>
                </tr>
            </thead>
            <tbody>
                {charges.map((c) => (
                    <tr key={c.key} className="border-t border-slate-100" data-testid={`coverage-row-${c.key}`}>
                        <td className="px-3 py-2">
                            <div className="text-slate-900">{c.label}</div>
                            <div className="mt-0.5 flex items-center gap-1.5"><BucketChip c={c} /><span className="text-[10px] text-slate-500">{c.option_label}</span></div>
                        </td>
                        <td className="px-3 py-2 text-right font-mono">{chargeAmount(c, currency)}</td>
                        <td className="px-3 py-2">
                            {c.allocations.length === 0 && <span className="text-emerald-700 font-medium" data-testid={`coverage-available-${c.key}`}>Available</span>}
                            {c.allocations.map((a) => (
                                <div key={a.id} className="flex items-center gap-2" data-testid={`coverage-alloc-${a.id}`}>
                                    <Link to={`/purchase-orders/${a.po_id}`} className="font-mono text-slate-800 hover:underline">{a.client_po_number || a.po_number}</Link>
                                    {a.po_line_label && <span className="text-slate-500">· {a.po_line_label}</span>}
                                    {a.coverage_start && <span className="text-slate-500">· {formatDate(a.coverage_start)} → {formatDate(a.coverage_end)}</span>}
                                    <button type="button" className="text-[10px] text-rose-600 hover:underline" onClick={() => onRelease(a)} data-testid={`coverage-release-${a.id}`}>Release</button>
                                </div>
                            ))}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function LegacyPoNotice() {
    return (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900 flex items-start gap-2" data-testid="legacy-po-notice">
            <WarningCircle size={14} weight="fill" className="mt-0.5 shrink-0" />
            This proposal was converted to a PO before charge-level tracking. Use “Link existing PO” to record which charges that PO covers — then the remaining charges become available for further POs.
        </div>
    );
}

export function PoProposalCoverageCard({ po }) {
    const rows = po?.proposal_coverage || [];
    if (!rows.length) return null;
    const cur = po.currency;
    const total = rows.reduce((s, a) => s + (Number(a.amount_excl_gst) || 0), 0);
    const byLine = new Map();
    for (const a of rows) {
        const k = a.po_line_id || "__whole__";
        if (!byLine.has(k)) byLine.set(k, []);
        byLine.get(k).push(a);
    }
    return (
        <div className="reedos-card p-5" data-testid="po-proposal-coverage">
            <div className="flex items-center justify-between gap-3 mb-3">
                <div>
                    <div className="reedos-eyebrow">Proposal charges covered by this PO</div>
                    <Link to={`/proposals/${rows[0].proposal_id}`} className="text-xs font-mono text-slate-700 hover:underline" data-testid="po-coverage-proposal-link">{rows[0].proposal_number}</Link>
                </div>
                <div className="text-right text-xs">
                    <div className="text-slate-500">Mapped / PO value (excl. GST)</div>
                    <div className="font-mono font-semibold" data-testid="po-coverage-total">
                        {formatINR(total, cur)} / {formatINR(po.billing_status?.po_excl_gst, cur)}
                    </div>
                </div>
            </div>
            <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-left">
                        <th className="px-3 py-2 reedos-eyebrow">PO line</th>
                        <th className="px-3 py-2 reedos-eyebrow">Proposal charge</th>
                        <th className="px-3 py-2 reedos-eyebrow">Period</th>
                        <th className="px-3 py-2 reedos-eyebrow text-right">Amount</th>
                    </tr>
                </thead>
                <tbody>
                    {[...byLine.entries()].map(([k, list]) => list.map((a, i) => (
                        <tr key={a.id} className={i === 0 ? "border-t border-slate-200" : ""} data-testid={`po-coverage-row-${a.id}`}>
                            <td className="px-3 py-1.5 font-medium text-slate-900">{i === 0 ? (a.po_line_label || (k === "__whole__" ? "Whole PO" : "—")) : ""}</td>
                            <td className="px-3 py-1.5"><span className="mr-1.5">{a.label}</span><BucketChip c={a} /></td>
                            <td className="px-3 py-1.5 text-slate-600">{a.coverage_start ? `${formatDate(a.coverage_start)} → ${formatDate(a.coverage_end)}` : "One-time"}</td>
                            <td className="px-3 py-1.5 text-right font-mono">{a.complimentary ? "Complimentary" : formatINR(a.amount_excl_gst, cur)}</td>
                        </tr>
                    )))}
                </tbody>
            </table>
        </div>
    );
}

export default function ProposalPoCoverage({ proposal, onChanged, onCoverage }) {
    const { data, err, reload } = useCoverage(proposal.id, [proposal.status, proposal.converted_to_po_id]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { onCoverage?.(data); }, [data]);
    if (err) return <div className="reedos-card p-4 text-xs text-rose-700">Linked POs unavailable: {err}</div>;
    if (!data) return null;
    const t = data.totals;
    const cur = proposal.currency;
    const refresh = () => { reload(); onChanged?.(); };
    const release = async (a) => {
        const note = window.prompt(`Release PO ${a.client_po_number || a.po_number} from "${a.label}"? Enter a reason (min 8 characters):`);
        if (!note) return;
        try {
            await api.post(`/billing/proposal/${proposal.id}/po-links/${a.id}/release`, { note });
            toast.success("Allocation released");
            reload();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };
    return (
        <div className="reedos-card p-5 space-y-4" data-testid="proposal-po-coverage">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="reedos-eyebrow">Linked POs &amp; charge coverage</div>
                    <p className="text-[11px] text-slate-500 mt-1">Charges from V{data.version_number}. Each client PO covers individual charges; remaining charges stay available.</p>
                </div>
                {data.linkable && (
                    <div className="flex gap-2">
                        <ConvertChargesDialog proposal={proposal} label="New PO from charges" testid="coverage-new-po-btn" onDone={refresh} />
                        <LinkPoDialog proposal={proposal} coverage={data} onDone={refresh} />
                    </div>
                )}
            </div>
            {data.legacy_unmapped_po_id && <LegacyPoNotice />}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Tile label="Proposal charges" value={formatINR(t.proposal_excl_gst, cur)} testid="coverage-total" />
                <Tile label="Covered by POs" value={formatINR(t.allocated_excl_gst, cur)} testid="coverage-allocated" />
                <Tile label="Still available" value={formatINR(t.available_excl_gst, cur)} testid="coverage-available-total" />
                <Tile
                    label="Reconciliation"
                    testid="coverage-reconciled"
                    value={t.reconciled
                        ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle size={14} weight="fill" /> Reconciled</span>
                        : <span className="inline-flex items-center gap-1 text-amber-700"><WarningCircle size={14} weight="fill" /> {formatINR(t.po_mapped_excl_gst, cur)} mapped</span>}
                />
            </div>
            <LinkedPoTable rows={data.linked_pos} currency={cur} />
            <ChargeCoverageTable charges={data.charges} currency={cur} onRelease={release} />
            {data.blank_items.length > 0 && (
                <p className="text-[11px] text-slate-500" data-testid="coverage-blank-items">
                    {data.blank_items.length} item{data.blank_items.length === 1 ? "" : "s"} carry no price in this version ({data.blank_items.map((b) => b.label).join(", ")}) — shown as not priced, not as Complimentary.
                </p>
            )}
        </div>
    );
}

function Tile({ label, value, testid }) {
    return (
        <div className="rounded-md border border-slate-200 bg-white px-3 py-2.5" data-testid={testid}>
            <div className="reedos-eyebrow text-[9px]">{label}</div>
            <div className="reedos-data font-display font-bold text-sm text-slate-900 mt-0.5">{value}</div>
        </div>
    );
}
